import {OpacityMaterial, EDLMaterial, ImageMaterial} from './materials';
import {EncompassPointCloud, PointCloudManager, RawPointCloud} from './pointcloud';
import {CompassControls, PanoControls} from './controls';
import {CameraList} from './cameras';
import {CameraMarkers} from './markers';
import {destroyWindowEvents, bindWindowEvent} from "../events";
import {AerialMap} from './aerial';
import {AssetType} from "../../redux/assets-slice";
import {Clamp, ColorMapKey, ColorType, MarkerNavType} from '../../redux/settings-slice';
import LocalScene, { DataProjectionCoordinate } from './projections';
import {nanoid} from '@reduxjs/toolkit';
import {colorMaps} from "../textures/colormaps";
import { Measurement } from './measurements/measurement';
import ChangeDetector from './utilities/change-detection';
import { isDevMode, isStaticSite } from '../../electron-modules';
import slash from 'slash';
import GPUPicker from './gpu-picker';
import { extentFromPoints } from './aerial/map';
import { debounce } from 'throttle-debounce';
import { SceneCameraState } from '../../redux/camera-slice';
import { ImageLabelTools } from './labelling';
import {
  AmbientLight,
  Camera,
  DepthTexture,
  FloatType,
  Vector3,
  Mesh,
  MeshBasicMaterial,
  NearestFilter,
  LinearFilter,
  PerspectiveCamera,
  PlaneBufferGeometry,
  RGBAFormat,
  Scene,
  UnsignedIntType,
  WebGLRenderer,
  WebGLRenderTarget,
  TextureLoader
} from "three";
import MeasurementController from './measurements/measurement-controller';
import { CameraLoadOptions } from './cameras/cameras';
import Stats from '../../stats.module.js';

export class Viewer {
    public readonly containerElement: HTMLElement;
    public defaultFarPlane: number = 10000;
    private defaultNearPlane: number = 0.1;
    private id = nanoid();
    public camera: PerspectiveCamera;
    private light: AmbientLight;
    public renderer: WebGLRenderer;
    private screenPass: RenderPass;
    private imageMaterial: ImageMaterial;
    private texture2DDepth: WebGLRenderTarget;
    private opacityMaterial: OpacityMaterial;
    private texture2DImage: any;
    private texture2DOpacity: WebGLRenderTarget;
    public gpuPickers: {
      markers: GPUPicker,
      points: GPUPicker
    };
    public imageLabels: ImageLabelTools;
    private EDLMaterial: EDLMaterial;
    private canvas: any;
    private changeDetector: ChangeDetector;
    public compass: CompassControls;
    public controls: PanoControls;
    public pointclouds: PointCloudManager;
    public cameraList: CameraList;
    public minimap: AerialMap;
    public markers: CameraMarkers;
    public measurements: MeasurementController;
    private stats: StatsPanel;
    private updateSavedState: Function;
    public showMessage: Function;
    private setCameraState: Function;
    private enabled = true;
    private focused = false;
    private drawPickerScene = false;
    public projectName;

    public timingDebug = {
      "markers": false,
      "scene": false
    };

    constructor(viewContainer, mapContainer, props) {
      const {showMessage, setCameraState} = props;

      this.containerElement = viewContainer;

      this.createScene();
      this.initGpuPickers();
      this.initResize();
      this.initMaterials();
      this.initViewEvents();

      this.showMessage = showMessage;
      this.setCameraState = setCameraState;

      this.compass = new CompassControls(this, props);
      this.pointclouds = new PointCloudManager(this);
      this.controls = new PanoControls(this, this.camera,
        this.renderer.domElement, props);
      this.measurements = new MeasurementController(this, props);
      this.cameraList = new CameraList(this);
      this.minimap = new AerialMap(this, mapContainer, props);
      this.imageLabels = new ImageLabelTools(this, props);
      this.markers = new CameraMarkers(this);
      this.changeDetector = new ChangeDetector(this);
      this.stats = new StatsPanel(this);

      // Add canvas elements to container
      this.addContainerElements();

      // Need to debounce to update ui smoothly
      this.updateSavedState = debounce(100, () => {
        this.calculateUpdatedValues();
      });

      // Force initial resize event
      this.handleWindowResize();

      // Start our animation loop
      this.animate();
    }

    get width(): number {
      return this.containerElement.offsetWidth;
    }

    get height(): number {
      return this.containerElement.offsetHeight;
    }

    get near() {
      return this.camera.near;
    }

    get far() {
      return this.camera.far;
    }

    get orthoList() {
      return this.minimap.orthoList;
    }

    get lookat() {
      let lookat = new Vector3(0,0, -1);
      let quat = this.camera.quaternion;
      lookat.applyQuaternion(quat);
      return lookat;
    }

    get renderTargetAttrs() {
      return {
        tDepth: this.renderTarget.depthTexture,
        colorMap: this.renderTarget.texture,
      };
    }

    get renderTarget() {
      return this.EDLSupport ? this.texture2DDepth : this.texture2DOpacity;
    }

    get renderMaterial() {
      return this.EDLSupport ? this.EDLMaterial : this.opacityMaterial;
    }

    get orbitState() {
      return this.controls.orbit.state;
    }

    get cameraSelected() {
      return this.cameraList.cameraLoaded;
    }

    get hasPoints() {
      return !this.pointclouds.isEmpty;
    }

    get hasCameras() {
      return !this.cameraList.isEmpty;
    }

    get isImageOnly() {
      return this.hasCameras && !this.hasPoints;
    }

    get isPointsOnly() {
      return !this.hasCameras && this.hasPoints;
    }

    get EDLSupport() : boolean {
      let capabilities = this.renderer.capabilities;
      let supported = (capabilities.maxVaryings > 8)
        && capabilities.floatFragmentTextures;

      return supported;
    }

    get isWebGL2(): boolean {
      let capabilities = this.renderer.capabilities;
      return capabilities.isWebGL2;
    }

    get expanded(): boolean {
      return this.width > this.minimap.width;
    }

    get calculatingSceneMarkers() {
      return this.markers.calculating;
    }

    get calculatingAerialMarkers() {
      return this.minimap.calculating;
    }

    get showCompassElement() {
      if (!LocalScene.initialized) {
        return false;
      }

      let show;

      if (this.isPointsOnly) {
        // Points only dataset. Always okay to show
        show = true;
      } else if (this.hasCameras) {
        // Current camera is done loading (or there is no
        // camera selected)
        let currentCamera = this.cameraList.current;
        show = currentCamera
          ? currentCamera.loaded
          : true;
      } else {
        // Conditions not met, hide navigation
        show = false;
      }

      return show;
    }

    /** Exposed Global Methods */
    setContainerCursor(cursor) {
      this.containerElement.style.cursor = cursor;
    }

    addContainerElements() {
      this.containerElement.appendChild(this.renderer.domElement);
      this.containerElement.appendChild(this.compass.domElement);
    }

    removeContainerElements() {
      this.containerElement.removeChild(this.renderer.domElement);
      this.containerElement.removeChild(this.compass.domElement);
    }

    setProjectName(name) {
      this.projectName = name;
    }

    togglePickerDebug(value) {
      this.drawPickerScene = value;
    }

    getAvailableClassifications(): number[] {
      return this.pointclouds.uniqueClassifications;
    }

    setVisibleClassifications(classifications) {
      classifications.forEach(classification => {
        this.pointclouds.setClassification(classification.id, classification.visible);
      });
    }

    setPointCloudOpacity(opacity: number) {
      this.pointclouds.transparency = opacity;
    }

    setPointCloudSize(size: number) {
      this.pointclouds.materialDefaults.size = size;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.size = size;
        });
      });
    }

    setPointCloudMaxPoints(points: number) {
      this.pointclouds.visiblePointsTarget = points;
    }

    setPointCloudDistance(distance: number) {
      let value = (distance === 100) ? this.far : distance * 10;
      this.pointclouds.materialDefaults.distance = value;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.distance = value;
        });
      });
    }

    setPointCloudHeightClip(clamp: Clamp) {
      this.pointclouds.updateClamping('heightClamp', 'height_clamp', clamp);
    }

    setPointCloudIntensityClip(clamp: Clamp) {
      this.pointclouds.updateClamping('intensityClamp', 'intensity_clamp', clamp);
    }

    setPointCloudColorMap(colorMapKey: ColorMapKey) {
      const textureData = colorMaps.find(colorMap => colorMap.id === colorMapKey).image;

      const loader = new TextureLoader();
      loader.load(textureData, texture => {
        this.pointclouds.materialDefaults.colorMap = colorMapKey;
        this.pointclouds.clouds.forEach(pointCloud => {
          pointCloud.materials.forEach(material => {
            material.color_map = texture;
            material.color_map.needsUpdate = true;
          });
        });
      });
    }

    setPointCloudColorType(colorType: ColorType) {
      this.pointclouds.materialDefaults.colorType = colorType;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.color_type = colorType;
        });
      });
    }

    setPointCloudDynamicSize(state: boolean) {
      this.pointclouds.materialDefaults.dynamicSize = state;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.dynamic_size = state;
        });
      });
    }

    setPointCloudCircularPoints(state: boolean) {
      this.pointclouds.materialDefaults.circularPoints = state;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.circularPoints = state;
        });
      });
    }

    setNavigationType(state: MarkerNavType) {
      this.markers.setNavigationType(state);
    }

    /** NOTE: Temporary(?) solution for the viewer being created over and over again */
    destroy() {
      destroyWindowEvents();

      // Destroy viewer elements
      this.enabled = false;
      this.camera = null;
      this.removeContainerElements();
      this.destroyViewEvents();

      // Destroy minimap dom element
      this.minimap.destroy();

      // TODO: destroy other things
    }

    setDataProjection(newProjection) {
      LocalScene.setDataProjection(newProjection);

      // Reset all measurements (not able to convert easily. would be easier if we changed
      // the encompass style data to be written in its original units, and converted on the fly
      if (this.controls.measurements.enabled) {
        this.controls.measurements.reset();
        this.controls.measurements.enable();
      }
    }

    setViewProjection(newProjection) {
      LocalScene.setViewProjection(newProjection);
    }

    setMeasurementUnits(units) {
      this.controls.measurements.setUnits(units);
    }

    setCameraVisible(cameraID, opts:CameraLoadOptions = {}) {
      this.cameraList.loadImage(cameraID, opts);
    }

    resetDefaultOrbitState() {
      this.controls.orbitDefaultApplied = false;
    }

    setOrbitDefault(numAdded, state: SceneCameraState) {
      if ((numAdded === 0) || this.controls.orbitDefaultApplied) {
        // Orbit state not applicable
        return false;
      }

      this.controls.orbitDefaultApplied = true;

      // Missing orbit rotation values
      if ((state.orbit === null) || (state.pivot === null)) {
        return false;
      }

      let orbit = new DataProjectionCoordinate(state.orbit)
        .toScene();

      let pivot = new DataProjectionCoordinate(state.pivot)
        .toScene();

      this.controls.setOrbitState(true, {
        position: orbit,
        pivot: pivot
      });

      return true;
    }

    toggleMeasurements(state) {
      if (state) {
        this.controls.measurements.enable();
      } else {
        this.controls.measurements.clear();
        this.controls.measurements.disable();
      }
    }

    toggleCameraAligner(state) {
      this.controls.cameraAlignerAdv.setState(state);
    }

    updateMeasureType(measureType) {
      this.controls.measurements.setMeasurementType(measureType);
    }

    updateMeasureHover(measurement: Measurement, state) {
      this.controls.measurements.updateRenderColor(measurement, state);
    }

    deleteMeasurement(measurement: Measurement) {
      this.controls.measurements.removeByID(measurement.id);
    }

    renameMeasurement(measurement: Measurement, title) {
      this.controls.measurements.setTitle(measurement.id, title);
    }

    exportMeasurements(type, ordering) {
      this.controls.measurements.export(type, ordering);
    }

    getMeasureExportType() {
      return this.controls.measurements.measureExportType;
    }

    loadMeasurementData(data) {
      return this.controls.measurements.loadMeasurementData(data);
    }

    /* Ortho Viewer Function Calls */
    setOrthoOpacity(value) {
      this.orthoList.setOpacity(value);
    }

    /* Labelling/Annotation function calls */
    activeImageLabelPaths() {
      return this.imageLabels.activeImagePaths();
    }

    loadedImageLabelPaths() {
      return this.imageLabels.loadedImagePaths();
    }

    async loadImageLabelData(data, progressCallback) {
      await this.imageLabels.loadImageLabelData(data, progressCallback);
    }

    setActiveLabelType(state) {
      this.imageLabels.setActiveLabelType(state);
    }

    checkIfActive() {
      return this.imageLabels.checkIfActive();
    }

    checkIfSelected() {
      return this.imageLabels.checkIfSelected();
    }

    labelHotkey(event) {
      this.imageLabels.onKeyDown(event);
    }

    addCategory(category) {
      this.imageLabels.addCategory(category);
    }

    deleteCategory(id) {
      this.imageLabels.deleteCategory(id);
    }

    deleteHighlighted() {
      this.imageLabels.deleteHighlighted();
    }

    /*addPolygon(polygon) {
      this.imageLabels.orthoLabels.drawPoly(polygon);
    }*/

    editCategoryName(id, name) {
      this.imageLabels.editCategoryName(id, name);
    }

    deleteAllCategories() {
      this.imageLabels.deleteAllCategories();
    }

    deleteLabel(id) {
      this.imageLabels.deleteLabel(id);
    }

    deletePoint(id) {
      this.imageLabels.deletePoint(id);
    }

    zoomToLabel(id) {
      this.imageLabels.zoomToLabel(id);
    }

    addLabel(id) {
      this.imageLabels.addLabel(id);
    }

    currentCategory() {
      return this.imageLabels.currentCategoryName;
    }

    selectLabel(id) {
      this.imageLabels.selectLabel(id);
    }

    completeLabel(label) {
      this.imageLabels.completeLabel(label);
    }

    unHighlight(id) {
      this.imageLabels.unHighlight(id);
    }

    unHighlightAll() {
      this.imageLabels.unHighlightAll();
    }

    numHighlighted() {
      return this.imageLabels.numHighlighted();
    }

    checkCategoryUniqueness(categoryName) {
      return this.imageLabels.checkUniqueName(categoryName);
    }

    labelObject = () => {
      return this.imageLabels.labelObject();
    }

    anyLabelSelected = () => {
      return this.imageLabels.checkIfSelected();
    }

    /* Asset Click functions */
    snapToOrtho(id) {
      const ortho = this.minimap.orthoList.getByID(id);
      if (!ortho) {
        return;
      }

      ortho.zoomToExtent();
    }

    snapToPointCloud(id) {
      const pointCloud = this.pointclouds.getByID(id);
      if (!pointCloud) {
        return;
      }

      let tightBoundingBox = pointCloud.tightBoundingBox;
      if (!tightBoundingBox) {
        return;
      }

      this.pointclouds.setLoadedOnce(false);
      this.pointclouds.zoomToExtent(tightBoundingBox, true, true);
    }

    snapToImages(id) {
      const cameras = this.cameraList.getByCSV(id);
      if (cameras.length === 0) {
        return;
      }

      let positions = cameras.map(camera => {
        return camera.position.value;
      });

      let extent = extentFromPoints(positions);
      this.minimap.setMapExtent(extent);
    }

    /* Camera Aligner Function Calls */
    resetCameraAligner() {
      this.controls.cameraAlignerAdv.reset();
    }

    checkIfAligned() {
      return this.cameraList.aligned;
    }

    addRowCameraAligner() {
      this.controls.cameraAlignerAdv.addRow();
    }

    setErrorsCameraAligner(errors) {
      this.controls.cameraAlignerAdv.setErrors(errors);
    }

    getErrorCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getError(i);
    }

    deleteRowCameraAligner(i) {
      this.controls.cameraAlignerAdv.deleteRow(i);
    }

    getImageObsCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getImageObs(i);
    }

    getPointObsCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getPointObs(i);
    }

    getCamPoseCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getCamPose(i);
    }

    getNumEntriesCameraAligner() {
      return this.controls.cameraAlignerAdv.numEntries;
    }

    getCamIdFromAligner(i) {
      return this.controls.cameraAlignerAdv.getCamId(i);
    }

    setBasicAlignerState(state) {
      this.controls.cameraAlignerBasic.setState(state);
    }

    resetBasicAlignerCamera(values = [0,0,0]) {
      this.controls.cameraAlignerBasic.reset(values);
    }

    getCamName(i) {
      return this.controls.cameraAlignerAdv.getCamName(i);
    }

    getCurrentCameraName() {
      return this.cameraList.current?.name;
    }

    setObservationTypeCameraAligner(type) {
      this.controls.cameraAlignerAdv.setObservationType(type);
    }

    setCurrentRowCameraAligner(rowNum) {
      this.controls.cameraAlignerAdv.setCurrentRow(rowNum);
    }

    flashObservations(rowNum) {
      this.controls.cameraAlignerAdv.flashObservations(rowNum);
    }

    hideCurrentObservation(type, rowNum) {
      this.controls.cameraAlignerAdv.hideCurrentObservation(type, rowNum);
    }

    getObservationPosition(rowNum) {
      return this.controls.cameraAlignerAdv.getObservationPosition(rowNum);
    }

    setAllObservationsVisible() {
      return this.controls.cameraAlignerAdv.setAllObservationsVisible();
    }


    /* End - Camera Aligner Function Calls */

    updateCompassOffset(offset) {
      this.compass.updateOffset(offset);
    }

    getCamerasForCSV = (assetID) => {
      return this.cameraList.getByCSV(assetID);
    }

    getCloudPercent(identifier) {
      let cloud = this.pointclouds.getByID(identifier);
      if (cloud && cloud instanceof RawPointCloud) {
        return cloud.percent;
      }

      return 0.0;
    }

    getCloudMissing(identifier) {
      let cloud = this.pointclouds.getByID(identifier);
      if (cloud) {
        return cloud.fileMissing;
      }

      return true;
    }

    getCloudIsLarge(identifier) {
      let cloud = this.pointclouds.getByID(identifier);
      if (cloud && cloud instanceof RawPointCloud) {
        return cloud.isLargeFile;
      }

      return false;
    }

    checkShiftReset() {
      const cameraList = this.cameraList.cameras;
      const cloudList = this.pointclouds.clouds;

      if ((cameraList.length === 0) && (cloudList.length === 0)) {
        LocalScene.resetSceneShift();
      }
    }

    reconcileCameras(camerasFiles, cameraHeight, cameraAdjustments, savedCameraState: SceneCameraState) {
      const existingCameras = this.cameraList.cameras;

      const allCameras = camerasFiles.map(cameraFile => {
        return cameraFile.data.map(camera => {
          return {
            ...camera,
            id: `${cameraFile.id}-${camera.name}`,
            csv: cameraFile.id,
            assetType: cameraFile.type,
            fromJSON: cameraFile.fromJSON
          };
        });
      }).flat();

      const activeCamera = savedCameraState.camera;
      const newCameras = new Set(allCameras.map(camera => camera.id));
      const currentCameras = new Set(existingCameras.map(camera => camera.id));
      const camerasToLoad = allCameras.filter(camera => !currentCameras.has(camera.id));
      const camerasToDelete = existingCameras.filter(camera => !newCameras.has(camera.id));

      const numRemoved = camerasToDelete.length;
      const numAdded = camerasToLoad.length;
      const numChanged = numRemoved + numAdded;

      // Delete existing cameras
      this.cameraList.deleteCameras(camerasToDelete);

      // Add all new cameras
      this.cameraList.addCameras(camerasToLoad,
        cameraAdjustments, cameraHeight);

      // Load saved camera from cameras state
      if (camerasToLoad.find(x => x.id === activeCamera)) {
        this.setCameraVisible(activeCamera, savedCameraState);
      }

      const cameraList = this.cameraList.cameras;
      const numCameras = cameraList.length;

      // Redraw cameras in scene and aerial view after changes
      if ((numRemoved > 0) || (numAdded > 0)) {
        this.minimap.addCameraMarkers(cameraList);
        this.markers.addCameraMarkers(cameraList);
        this.controls.measurements.refreshMeasurementList();
      }

      // Update arrow marker navigation
      if ((numRemoved > 0) || ((numAdded > 0) && (numAdded < numCameras))) {
        this.markers.updateArrowNavigation();
      }

      // All cameras have been removed
      if ((numCameras === 0) && (numRemoved > 0)) {
        this.cameraList.setOrbitCamera();
        this.minimap.setActiveCamera();
        this.markers.clearArrowNavigation();
        return;
      }

      // Apply default orbit position if possible
      let applied = this.setOrbitDefault(numAdded, savedCameraState);
      if (applied) {
        return;
      }

      // Camera changed (added or removed) and nothing visible.
      // Switch to the first camera in our new camera list instead
      if ((numChanged > 0) && (!this.cameraList.cameraLoaded)) {
        if (cameraList.length > 0) {
          this.setCameraVisible(cameraList[0].id);
        }
      }
    }

    reconcilePointClouds(pointClouds, savedCameraState: SceneCameraState) {
      const cloudList = this.pointclouds.clouds;
      const currentPointClouds = new Set(cloudList.map(cloud => cloud.identifier));
      const newPointClouds = new Set(pointClouds.map(cloud => cloud.id));

      const pointCloudsToLoad = pointClouds.filter(cloud => !currentPointClouds.has(cloud.id));
      const pointCloudsToDelete = cloudList.filter(cloud => !newPointClouds.has(cloud.identifier));

      const numRemoved = pointCloudsToDelete.length;
      const numAdded = pointCloudsToLoad.length;

      // Delete clouds that need to be removed
      pointCloudsToDelete.forEach(pointCloud => {
        this.pointclouds.deletePointCloud(pointCloud.identifier);
      });

      // Add new pointclouds
      pointCloudsToLoad.forEach(pointCloud => {
        const cloudDetails = {
          name: pointCloud.name,
          identifier: pointCloud.id,
          path: slash(pointCloud.data.path),
          visible: pointCloud.visible,
          remote: isStaticSite,
          fromJSON: pointCloud.fromJSON
        };

        if (pointCloud.type === AssetType.LAS) {
          let pointCloud = new RawPointCloud(this.pointclouds);
          pointCloud.loadFile(cloudDetails);
        } else if (pointCloud.type === AssetType.Encompass) {
          let pointCloud = new EncompassPointCloud(this.pointclouds);
          pointCloud.loadNodes(cloudDetails);
        }
      });

      // Toggle proper visibility for each cloud
      pointClouds.forEach(pointCloud => {
        this.pointclouds.toggleVisibility(pointCloud.id, pointCloud.visible);
      });

      const numClouds = this.pointclouds.clouds.length;
      const numCameras = this.cameraList.cameras.length;

      // Apply default orbit position if possible
      let applied = this.setOrbitDefault(numAdded, savedCameraState);
      if (applied) {
        return;
      }

      // No cloud remain. Exit 3d mode if cameras exist
      if ((numClouds === 0) && (numRemoved > 0) && (numCameras > 0)) {
        this.controls.setOrbitState(false);
      }
    }

    reconcileOrthos(orthos) {
      const orthosList = this.orthoList.orthos;
      const currentOrthos = new Set(orthosList.map(ortho => ortho.id));
      const newOrthos = new Set(orthos.map(ortho => ortho.id));

      const orthosToLoad = orthos.filter(ortho => !currentOrthos.has(ortho.id));
      const orthosToDelete = orthosList.filter(ortho => !newOrthos.has(ortho.id));

      // Delete orthos that need to be removed
      orthosToDelete.forEach(ortho => {
        this.orthoList.deleteOrtho(ortho.id);
      });

      // Add new orthos
      orthosToLoad.forEach(ortho => {
        const {imageBounds, imageData, imageSize, imageSizeOriginal, imagePath,
          scaleX, scaleY} = ortho.data;

        const orthoInfo = {
          id: ortho.id,
          name: ortho.name,
          corners: imageBounds,
          base64: imageData,
          imageSize: imageSize,
          imageSizeOriginal: imageSizeOriginal,
          fromJSON: ortho.fromJSON,
          imagePath: imagePath,
          scaleX: scaleX,
          scaleY: scaleY
        };

        this.orthoList.addNewOrtho(orthoInfo);
      });

      // Toggle proper visibility for each ortho
      orthos.forEach(ortho => {
        this.orthoList.toggleVisibility(ortho.id, ortho.visible);
      });
    }

    setCameraHeights(height) {
      this.cameraList.setCameraHeights(height);
    }

    setCameraAdjustments(adjustments) {
      this.cameraList.setCameraAdjustments(adjustments);
    }

    setCameraMatrix(matrix) {
      this.cameraList.setCameraMatrix(matrix);
      if (this.controls.cameraAlignerAdv.enabled){
        this.controls.cameraAlignerAdv.redrawImageObsMarkers();
      }
    }

    resetCameraMatrix() {
      this.cameraList.resetCameraMatrix();
      if (this.controls.cameraAlignerAdv.enabled){
        this.controls.cameraAlignerAdv.redrawImageObsMarkers();
      }
    }

    resetLoadedOnce() {
      this.pointclouds.setLoadedOnce(false);
    }

    resetDefaultAerialState(state) {
      this.minimap.map.updateSize();
      this.minimap.reset(state);
    }

    removeInteractions() {
      const measurements = this.controls.measurements;
      if (measurements.enabled) {
        measurements.measurementAerialView.removeInteraction();
      }
    }

    /** Private Methods */
    onKeyDown(event) {
      if (!this.focused) {
        return;
      }

      event.preventDefault();
      this.cameraList.onKeyDown(event);
      this.controls.cameraAlignerBasic.onKeyDown(event);
    }

    onMouseFocus(event) {
      if (event.type === 'mouseover') {
        this.focused = true;
      } else if (event.type === 'mouseout') {
        this.focused = false;
      }
    }

    initViewEvents() {
      this.onMouseFocus = this.onMouseFocus.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      // Keep track of our canvas focus
      this.containerElement.addEventListener( 'mouseover', this.onMouseFocus, false );
      this.containerElement.addEventListener( 'mouseout', this.onMouseFocus, false );

      // Keydown doesnt work on canvas
      window.addEventListener( 'keydown', this.onKeyDown, false) ;
    }

    destroyViewEvents() {
      this.containerElement.removeEventListener( 'mouseover', this.onMouseFocus, false );
      this.containerElement.removeEventListener( 'mouseout', this.onMouseFocus, false );
      window.removeEventListener( 'keydown', this.onKeyDown, false) ;
    }

    updatePickers() {
      Object.keys(this.gpuPickers).forEach(name => {
        this.updatePicker(name);
      });
    }

    updatePicker(name) {
      this.gpuPickers[name].needsUpdate = true;
    }

    updatePickerCamera() {
      const pickers = Object.values(this.gpuPickers);
      pickers.forEach(picker => {
        picker.setCamera(this.camera);
      });
    }

    createScene() {
      this.camera = new PerspectiveCamera(90, 1.0,
        this.defaultNearPlane, this.defaultFarPlane);

      this.renderer = new WebGLRenderer({
        antialias: false,
        alpha: true,
        logarithmicDepthBuffer: false,
        powerPreference: "high-performance"
      });

      if (this.isWebGL2) {
        console.log("WebGL2 Detected");
      }

      this.renderer.autoClear = false;
    }

    initGpuPickers() {
      this.gpuPickers = {
        markers: new GPUPicker(this, "markers", false),
        points: new GPUPicker(this, "points", false)
      };

      for (const picker of Object.values(this.gpuPickers)) {
        picker.setCamera(this.camera);
      }
    }

    initMaterials() {
      // Create our render pass
      this.screenPass = new RenderPass();

      // Default render target / material
      this.opacityMaterial = new OpacityMaterial();
      this.texture2DOpacity = new WebGLRenderTarget(
        this.width, this.height, {
          format: RGBAFormat,
          minFilter: NearestFilter,
          magFilter: NearestFilter,
          stencilBuffer: false
        }
      );

      // Material for rendering panoramic as background
      this.imageMaterial = new ImageMaterial();
      this.texture2DImage = new WebGLRenderTarget(
        this.width, this.height, {
          minFilter: LinearFilter,
          magFilter: LinearFilter,
          format: RGBAFormat,
          stencilBuffer: false
        }
      );

      if (!this.EDLSupport) {
        console.log("EDL Not Supported");
        return;
      }

      // EDL render target / material
      console.log("EDL Supported");
      this.EDLMaterial = new EDLMaterial(this);
      this.texture2DDepth = new WebGLRenderTarget(
        this.width, this.height, {
          format: RGBAFormat,
          minFilter: NearestFilter,
          magFilter: NearestFilter,
          generateMipmaps: false,
          stencilBuffer: false,
          depthBuffer: true
        }
      );

      let texturePrecision = this.isWebGL2 ? FloatType : UnsignedIntType;
      this.texture2DDepth.depthTexture = new DepthTexture(0, 0);
      this.texture2DDepth.depthTexture.type = texturePrecision;
    }

    initResize() {
      bindWindowEvent('resize', () => {
        this.handleWindowResize();
      });
    }

    handleWindowResize() {
      let width = this.containerElement.clientWidth;
      let height = this.containerElement.clientHeight;
      this.resizeCanvas(width, height);
      this.updatePickers();
    }

    resizeCanvas(width, height) {
      let aspect = width / height;

      this.camera.aspect = aspect;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
      this.compass.setSize(width, height);

      if (this.EDLSupport) {
        this.EDLMaterial.screenWidth = width;
        this.EDLMaterial.screenHeight = height;
      }
    }

    resizeRenderTargets() {
      let width = this.width;
      let height = this.height;


      // Resize render targets
      let canvas = [
        this.texture2DDepth,
        this.texture2DOpacity,
      ];

      canvas.forEach(c => {
        if (!c) {
          return;
        }

        let w = c.width;
        let h = c.height;
        if ((w !== width) || (h !== height)) {
          c.dispose();
          c.setSize(width, height);
        }
      });

      // Resize gpu picker targets
      const pickers = Object.values(this.gpuPickers);
      pickers.forEach(picker => {
        picker.resizeTarget(width, height);
      });
    }

    updateMaterial(material, values) {
      let keys = Object.keys(values);
      keys.forEach(key => {
        material[key] = values[key];
      });
    }

    setRenderTarget(target, clear = false) {
      this.renderer.setRenderTarget(target);
      if (clear) {
        this.renderer.clear();
      }
    }

    renderWithTransparency(scenes, opacity, clear=true) {
      this.setRenderTarget(this.renderTarget, clear);
      scenes.forEach(scene => {
        if (!scene) {
          return;
        }

        this.renderer.render(scene, this.camera);
      });

      this.updateMaterial(this.renderMaterial, {
        ...this.renderTargetAttrs, opacity: opacity
      });
      this.screenPass.render(this.renderer, this.renderMaterial);
    }

    renderPanoramic() {
      // Render panoramic image
      this.setRenderTarget(this.texture2DImage, true);
      this.renderer.render(this.cameraList.scene, this.camera);

      // Update material texture with results
      this.updateMaterial(this.imageMaterial, {
        colorMap: this.texture2DImage.texture
      });

      // Render to screen and clear depth
      this.screenPass.render(this.renderer, this.imageMaterial);
      this.renderer.clearDepth();
    }

    renderSceneObjects() {
      // Render objects at full opacity
      this.renderWithTransparency([
        this.markers.renderInFront ? null : this.markers.scene
      ], 1.0);

      // Render pointcloud with transparency
      this.renderWithTransparency([this.pointclouds.scene],
        this.pointclouds.transparency, false);

      // Render scene labels (mixed opacity)
      let sceneLabels = this.imageLabels.sceneLabels;
      this.renderWithTransparency([sceneLabels.opacityScene],
        sceneLabels.opacity);
      this.renderWithTransparency([sceneLabels.standardScene], 1.0);

      // Semi-transparent markers sometimes rendered on top of everything
      if (this.markers.renderInFront) {
        this.renderWithTransparency([this.markers.scene],
          this.markers.opacity);
      }

      // Items here are rendered on top of everything, so they can always be seen
      this.renderWithTransparency([
        this.measurements.scene,
        this.controls.alignerScene
      ], 1.0);
    }

    renderPickerScene() {
      this.gpuPickers.points.needsUpdate = true;
      this.gpuPickers.points.update();
      this.setRenderTarget(null, true);
      this.renderer.render(this.gpuPickers.points.scene, this.camera);
    }

    updateSceneElements() {
      this.changeDetector.update();
      this.pointclouds.update();
      this.measurements.update(this.changeDetector);
      this.controls.update(this.changeDetector);
      this.cameraList.update();
      this.minimap.update(this.changeDetector);
      this.markers.update();
      this.imageLabels.update(this.changeDetector);
    }

    calculateUpdatedValues() {
      let state = {
        camera: null,
        fov: null,
        angles: null,
        orbit: null,
        pivot: null
      } as SceneCameraState;

      if (this.orbitState) {
        // Update for orbit camera
        let {orbit, pivot} = this.controls.orbit
          .getPositionValues();

        state = {
          ...state,
          orbit: orbit,
          pivot: pivot
        };
      } else if (this.cameraList.current) {
        // Update for standard camera
        state = {
          ...state,
          camera: this.cameraList.current.id,
          fov: this.controls.fov,
          angles: this.controls.angles
        };
      }

      this.setCameraState(state);
    }

    animate() {
      if (!this.enabled) {
        return;
      }

      this.stats.begin();

      this.resizeRenderTargets();
      this.updateSceneElements();

      if (this.changeDetector.changed) {
        this.updateSavedState();
      }

      if (this.drawPickerScene) {
        this.renderPickerScene();
      } else {
        this.renderPanoramic();
        this.renderSceneObjects();
      }

      // Render compass element
      this.compass.render(this.showCompassElement,
        this.camera, this.orbitState);

      this.stats.end();

      requestAnimationFrame(() => {
        this.animate();
      });
    }
}

class RenderPass {
    private readonly screenScene: Scene;
    private readonly screenQuad: Mesh;
    private readonly camera: Camera;

    constructor() {
      const geometry = new PlaneBufferGeometry(2, 2, 0);
      const material = new MeshBasicMaterial({
        depthTest: true,
        depthWrite: true,
        transparent: true
      });

      this.screenScene = new Scene();
      this.screenQuad = new Mesh(geometry, material);
      this.screenScene.add(this.screenQuad);
      this.camera = new Camera();
    }

    /** Render to screen. Note that our render target is changed after using this function */
    render(renderer, material, target = null) {
      this.screenQuad.material = material;
      renderer.setRenderTarget(target);
      renderer.render(this.screenScene, this.camera);
    }
}

class StatsPanel {
  private stats: Stats;
  private viewer: Viewer;
  private nodesVisible;
  private pointsVisible;
  private enabled = isDevMode;
  private expanded = false;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.clear();
    this.init();
  }

  get indexToShow() {
    let childCount = this.stats.dom.children.length;
    return [0, childCount-1, childCount-2];
  }

  clear() {
    // Remove old stats panels
    [...document.getElementsByClassName("stats")].forEach(element => {
      element.remove();
    });
  }

  init() {
    if (!this.enabled) {
      return;
    }

    this.stats = new Stats();
    this.stats.dom.classList.add("stats");
    this.setStyle();

    this.pointsVisible = this.stats.addPanel(
      new Stats.Panel('', '#ff8', '#221'));

    this.nodesVisible = this.stats.addPanel(
      new Stats.Panel('NODE', '#f8f', '#212'));

    [...this.stats.dom.children].forEach(element => {
      element.style.display = 'none';
      element.style.padding = '2px';
    });

    this.drawExpandedState();

    document.getElementById("debug-placeholder")
      .appendChild(this.stats.dom);

    this.initEvents();
  }

  drawExpandedState() {
    // Hide all elements
    [...this.stats.dom.children].forEach(element => {
      element.style.display = 'none';
    });

    this.indexToShow.forEach(index => {
      this.stats.dom.children[index].style.display = 'inline-block';
    });

    if (this.expanded) {
      [...this.stats.dom.children].forEach(element => {
        element.style.display = 'inline-block';
      });
    }
  }

  initEvents() {
    this.stats.dom.onclick = (event) => {
      event.preventDefault();
      this.expanded = !this.expanded;
      this.drawExpandedState();
    };
  }

  setStyle() {
    this.stats.dom.style.position = "";
    this.stats.dom.style.top = "";
    this.stats.dom.style.left = "";
    this.stats.dom.style.bottom = "0px";
    this.stats.dom.style['z-index'] = "";
  }

  begin() {
    if (!this.enabled) {
      return;
    }

    this.stats.begin();
  }

  end() {
    if (!this.enabled) {
      return;
    }

    let pointCloud = this.viewer.pointclouds;
    let numberNodes = pointCloud.numSceneNodesVisible;
    let visiblePoints = pointCloud.numVisiblePoints;
    let maxPoints = pointCloud.visiblePointsTarget;

    this.pointsVisible.update(
      visiblePoints / 1000,
      maxPoints / 1000
    );

    this.nodesVisible.update(numberNodes, 500);

    this.stats.end();
  }
}


export const withPerformanceMeasure = (name, callback) => {
  let response;

  if (isDevMode) {
    let timeStart = performance.now();

    response = callback();

    let timeFinish = performance.now();
    let timeElapsed = timeFinish - timeStart;
    let timeUntilWarning = 10;
    if (timeElapsed > timeUntilWarning) {
      console.warn(`Slow rendering detected: ${name} took ${timeElapsed}ms`);
    }
  } else {
    response = callback();
  }

  return response;
};
